import React, { useEffect, useMemo, useState, useCallback } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Tabs, TabsContent, TabsList, TabsTrigger } from './ui/tabs';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from './ui/dialog';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from './ui/select';
import { Textarea } from './ui/textarea';
import { FinancialSyncAudit } from './FinancialSyncAudit';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const authHeader = () => ({
  Authorization: `Bearer ${localStorage.getItem('token')}`,
});

const fmtDT = (v) => {
  if (!v) return '—';
  try {
    return new Date(v).toLocaleString('ar-DZ', {
      year: 'numeric', month: '2-digit', day: '2-digit',
      hour: '2-digit', minute: '2-digit',
    });
  } catch { return String(v); }
};
const fmtMoney = (n, ccy = 'DZD') =>
  `${(Number(n) || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} ${ccy}`;

const OPERATION_TYPES = [
  { value: 'daily_operation',  label: 'عملية يومية' },
  { value: 'invoice_payment',  label: 'دفع فاتورة' },
  { value: 'ticket_sale',      label: 'بيع تذكرة' },
  { value: 'trip_sale',        label: 'بيع رحلة' },
  { value: 'payment',          label: 'دفعة' },
  { value: 'expense',          label: 'مصروف' },
  { value: 'refund',           label: 'استرجاع' },
  { value: 'other',            label: 'أخرى' },
];

const PAYMENT_METHODS = [
  { value: 'cash',   label: '💵 نقدًا' },
  { value: 'bank',   label: '🏦 تحويل بنكي' },
  { value: 'card',   label: '💳 بطاقة' },
  { value: 'cheque', label: '📝 شيك' },
];

const STATUS_META = {
  created:      { label: 'منشأ',       cls: 'bg-blue-100 text-blue-800 border-blue-300',     icon: '🆕' },
  validated:    { label: 'مُصادق عليه', cls: 'bg-amber-100 text-amber-800 border-amber-300',  icon: '✅' },
  transferred:  { label: 'محوَّل',      cls: 'bg-purple-100 text-purple-800 border-purple-300', icon: '🚚' },
  received:     { label: 'مُستلَم',     cls: 'bg-green-100 text-green-800 border-green-300',  icon: '🏦' },
  cancelled:    { label: 'مُلغى',       cls: 'bg-gray-200 text-gray-700 border-gray-300',     icon: '✖️' },
};

const BATCH_STATUS_META = {
  open:      { label: 'مفتوح',     cls: 'bg-blue-100 text-blue-800 border-blue-300' },
  confirmed: { label: 'مؤكَّد',     cls: 'bg-amber-100 text-amber-800 border-amber-300' },
  received:  { label: 'مُستلَم',    cls: 'bg-green-100 text-green-800 border-green-300' },
  cancelled: { label: 'مُلغى',     cls: 'bg-gray-200 text-gray-700 border-gray-300' },
};

const StatusBadge = ({ status, map = STATUS_META }) => {
  const m = map[status] || { label: status, cls: 'bg-gray-100 text-gray-700 border-gray-300' };
  return (
    <span className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-xs font-semibold border ${m.cls}`}
          data-testid={`status-badge-${status}`}>
      {m.icon || ''} {m.label}
    </span>
  );
};

/* ════════════════════════════════════════════════════════════════════════
   Timeline Component — visualises Created → Validated → Transferred → Received
   ════════════════════════════════════════════════════════════════════════ */
const Timeline = ({ entry }) => {
  const steps = [
    { key: 'created',     label: 'إنشاء',     at: entry.created_at,     by: entry.employee_name },
    { key: 'validated',   label: 'مصادقة',    at: entry.validated_at,   by: entry.validated_by },
    { key: 'transferred', label: 'تحويل',     at: entry.transferred_at, by: entry.transferred_by },
    { key: 'received',    label: 'استلام',    at: entry.received_at,    by: entry.received_by },
  ];
  return (
    <div className="flex items-center gap-2 flex-wrap" data-testid="entry-timeline">
      {steps.map((s, i) => {
        const done = !!s.at;
        return (
          <React.Fragment key={s.key}>
            <div className={`flex items-center gap-2 px-3 py-1.5 rounded-lg border ${
              done ? 'bg-green-50 border-green-300 text-green-800' : 'bg-gray-50 border-gray-200 text-gray-400'
            }`}>
              <span className="text-lg">{done ? '✓' : '○'}</span>
              <div className="text-xs leading-tight">
                <div className="font-bold">{s.label}</div>
                <div className="opacity-75">{done ? fmtDT(s.at) : '—'}</div>
              </div>
            </div>
            {i < steps.length - 1 && (
              <div className={`h-0.5 w-6 ${done ? 'bg-green-400' : 'bg-gray-200'}`} />
            )}
          </React.Fragment>
        );
      })}
    </div>
  );
};

/* ════════════════════════════════════════════════════════════════════════
   New Entry Dialog
   ════════════════════════════════════════════════════════════════════════ */
const NewEntryDialog = ({ open, onClose, onCreated }) => {
  const [form, setForm] = useState({
    operation_type: 'daily_operation', amount: '', client_name: '',
    payment_method: 'cash', description: '',
  });
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!form.amount || Number(form.amount) <= 0) {
      toast.error('أدخل مبلغًا صالحًا');
      return;
    }
    setSubmitting(true);
    try {
      const payload = { ...form, amount: Number(form.amount) };
      const r = await axios.post(`${API}/financial/entries`, payload, { headers: authHeader() });
      toast.success(`✅ تم إنشاء الوصل ${r.data.receipt_no}`);
      onCreated && onCreated(r.data);
      setForm({ operation_type: 'daily_operation', amount: '', client_name: '', payment_method: 'cash', description: '' });
      onClose();
    } catch (e) {
      toast.error('فشل الإنشاء: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" dir="rtl">
        <DialogHeader>
          <DialogTitle>➕ إنشاء وصل مالي جديد</DialogTitle>
          <DialogDescription>سيتم إنشاء وصل غير قابل للتعديل مع رقم تسلسلي تلقائي.</DialogDescription>
        </DialogHeader>
        <div className="grid gap-3">
          <div>
            <Label>نوع العملية</Label>
            <Select value={form.operation_type}
                    onValueChange={(v) => setForm({ ...form, operation_type: v })}>
              <SelectTrigger data-testid="new-entry-op-type"><SelectValue /></SelectTrigger>
              <SelectContent>
                {OPERATION_TYPES.map((o) => (
                  <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>المبلغ (دج)</Label>
            <Input type="number" min="0" step="0.01" value={form.amount}
                   onChange={(e) => setForm({ ...form, amount: e.target.value })}
                   data-testid="new-entry-amount" />
          </div>
          <div>
            <Label>اسم العميل (اختياري)</Label>
            <Input value={form.client_name}
                   onChange={(e) => setForm({ ...form, client_name: e.target.value })}
                   data-testid="new-entry-client" />
          </div>
          <div>
            <Label>طريقة الدفع</Label>
            <Select value={form.payment_method}
                    onValueChange={(v) => setForm({ ...form, payment_method: v })}>
              <SelectTrigger data-testid="new-entry-payment"><SelectValue /></SelectTrigger>
              <SelectContent>
                {PAYMENT_METHODS.map((p) => (
                  <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label>ملاحظات</Label>
            <Textarea rows={2} value={form.description}
                      onChange={(e) => setForm({ ...form, description: e.target.value })}
                      data-testid="new-entry-description" />
          </div>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button onClick={submit} disabled={submitting}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="new-entry-submit">
            {submitting ? 'جارٍ الحفظ...' : '💾 حفظ'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ════════════════════════════════════════════════════════════════════════
   Create Batch Dialog
   ════════════════════════════════════════════════════════════════════════ */
const CreateBatchDialog = ({ open, onClose, selectedEntries, onCreated }) => {
  const [notes, setNotes] = useState('');
  const [recipient, setRecipient] = useState('');
  const [confirmNow, setConfirmNow] = useState(true);
  const [submitting, setSubmitting] = useState(false);

  const total = selectedEntries.reduce((s, e) => s + Number(e.amount || 0), 0);

  const submit = async () => {
    if (!selectedEntries.length) return;
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/financial/batches`, {
        entry_ids: selectedEntries.map((e) => e.id),
        notes, recipient_name: recipient || null, confirm_now: confirmNow,
      }, { headers: authHeader() });
      toast.success(`✅ تم إنشاء الحزمة ${r.data.reference_no} — ${fmtMoney(r.data.total_amount)}`);
      onCreated && onCreated(r.data);
      setNotes(''); setRecipient(''); setConfirmNow(true);
      onClose();
    } catch (e) {
      toast.error('فشل: ' + (e?.response?.data?.detail || e.message));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-2xl" dir="rtl">
        <DialogHeader>
          <DialogTitle>🚚 إنشاء حزمة تحويل ({selectedEntries.length} وصل)</DialogTitle>
          <DialogDescription>راجع الوصولات قبل التأكيد. سيتم قفل الوصولات المُحدَّدة فور إنشاء الحزمة.</DialogDescription>
        </DialogHeader>
        <div className="space-y-4">
          <div className="bg-amber-50 border-2 border-amber-300 rounded-xl p-4 flex justify-between items-center">
            <div>
              <div className="text-sm text-amber-900">إجمالي الحزمة</div>
              <div className="text-3xl font-bold text-amber-700" data-testid="batch-total-preview">
                {fmtMoney(total)}
              </div>
            </div>
            <div className="text-sm text-amber-800">
              عدد الوصولات: <b>{selectedEntries.length}</b>
            </div>
          </div>
          <div className="max-h-44 overflow-auto border rounded-lg">
            <table className="w-full text-xs">
              <thead className="bg-gray-50 sticky top-0">
                <tr>
                  <th className="p-2 text-start">رقم الوصل</th>
                  <th className="p-2 text-start">العميل</th>
                  <th className="p-2 text-start">النوع</th>
                  <th className="p-2 text-end">المبلغ</th>
                </tr>
              </thead>
              <tbody>
                {selectedEntries.map((e) => (
                  <tr key={e.id} className="border-t">
                    <td className="p-2 font-mono">{e.receipt_no}</td>
                    <td className="p-2">{e.client_name || '—'}</td>
                    <td className="p-2">{(e.operation_type || '').replace('_', ' ')}</td>
                    <td className="p-2 text-end font-semibold">{fmtMoney(e.amount, '')}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <Label>اسم المُستلِم (السائق / المحاسب)</Label>
            <Input value={recipient} onChange={(e) => setRecipient(e.target.value)}
                   data-testid="batch-recipient" placeholder="مثال: علي السائق" />
          </div>
          <div>
            <Label>ملاحظات</Label>
            <Textarea rows={2} value={notes} onChange={(e) => setNotes(e.target.value)}
                      data-testid="batch-notes" />
          </div>
          <label className="flex items-center gap-2 text-sm cursor-pointer">
            <input type="checkbox" checked={confirmNow}
                   onChange={(e) => setConfirmNow(e.target.checked)}
                   data-testid="batch-confirm-now" />
            تأكيد التسليم الفوري (تنتقل الوصولات لحالة «محوَّل»)
          </label>
        </div>
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>إلغاء</Button>
          <Button onClick={submit} disabled={submitting}
                  className="bg-amber-600 hover:bg-amber-700 text-white"
                  data-testid="batch-create-submit">
            {submitting ? 'جارٍ الحفظ...' : '🚚 إنشاء الحزمة'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ════════════════════════════════════════════════════════════════════════
   Batch Detail Dialog (Finance opens this to view & receive)
   ════════════════════════════════════════════════════════════════════════ */
const BatchDetailDialog = ({ open, batchId, onClose, currentUser, onChanged }) => {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [busy, setBusy] = useState(false);

  const canManage = currentUser?.role === 'super_admin' || currentUser?.role === 'general_accountant';

  const load = useCallback(async () => {
    if (!batchId) return;
    setLoading(true);
    try {
      const r = await axios.get(`${API}/financial/batches/${batchId}`, { headers: authHeader() });
      setData(r.data);
    } catch (e) {
      toast.error('فشل تحميل الحزمة');
    } finally {
      setLoading(false);
    }
  }, [batchId]);

  useEffect(() => { if (open) load(); }, [open, load]);

  const doReceive = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/financial/batches/${batchId}/receive`, {}, { headers: authHeader() });
      toast.success('✅ تم تأكيد الاستلام');
      onChanged && onChanged();
      load();
    } catch (e) {
      toast.error('فشل: ' + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const doConfirm = async () => {
    setBusy(true);
    try {
      await axios.post(`${API}/financial/batches/${batchId}/confirm`, {}, { headers: authHeader() });
      toast.success('✅ تم التأكيد');
      onChanged && onChanged();
      load();
    } catch (e) {
      toast.error('فشل: ' + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const doCancel = async () => {
    const reason = window.prompt('سبب الإلغاء؟', '');
    if (reason === null) return;
    setBusy(true);
    try {
      await axios.post(`${API}/financial/batches/${batchId}/cancel`, { reason }, { headers: authHeader() });
      toast.success('🗑️ تم إلغاء الحزمة');
      onChanged && onChanged();
      load();
    } catch (e) {
      toast.error('فشل: ' + (e?.response?.data?.detail || e.message));
    } finally { setBusy(false); }
  };

  const print = () => {
    const token = localStorage.getItem('token') || '';
    // The print endpoint requires Authorization header (no token query). Open the URL — server allows
    // GET with bearer token through `get_current_user`. For convenience, copy URL with token-free print:
    // since the browser will not send Authorization on window.open, we fetch then write blob.
    (async () => {
      try {
        const r = await axios.get(`${API}/financial/batches/${batchId}/print`,
          { headers: authHeader(), responseType: 'text' });
        const w = window.open('', '_blank', 'noopener,noreferrer');
        w.document.open();
        w.document.write(r.data);
        w.document.close();
        setTimeout(() => {
          try { w.print(); }
          catch (err) { console.warn('[FinancialTraceability] batch print() failed:', err?.message || err); }
        }, 600);
      } catch (e) {
        toast.error('فشل الطباعة');
      }
    })();
  };

  if (!open) return null;
  const b = data?.batch || {};
  const entries = data?.entries || [];

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-5xl max-h-[90vh] overflow-y-auto" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between gap-3 flex-wrap">
            <span>🧾 تفاصيل حزمة التحويل {b.reference_no || ''}</span>
            {b.status && <StatusBadge status={b.status} map={BATCH_STATUS_META} />}
          </DialogTitle>
          <DialogDescription>عرض كامل لجميع الوصولات المرتبطة بهذه الحزمة وحالة كل منها.</DialogDescription>
        </DialogHeader>

        {loading ? (
          <div className="p-8 text-center text-gray-500">جاري التحميل...</div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">الوكالة</div>
                <div className="font-bold">{b.agency_name || '—'}</div>
              </div>
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3">
                <div className="text-xs text-amber-700">الإجمالي</div>
                <div className="font-bold text-lg" data-testid="batch-detail-total">
                  {fmtMoney(b.total_amount, b.currency || 'DZD')}
                </div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">عدد الوصولات</div>
                <div className="font-bold">{b.entries_count}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">المُرسِل</div>
                <div className="font-bold">{b.created_by_name || '—'}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">المُستلِم</div>
                <div className="font-bold">{b.recipient_name || '—'}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">تاريخ الإنشاء</div>
                <div className="font-bold text-xs">{fmtDT(b.created_at)}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">تاريخ التأكيد</div>
                <div className="font-bold text-xs">{fmtDT(b.confirmed_at)}</div>
              </div>
              <div className="bg-gray-50 rounded-lg p-3">
                <div className="text-xs text-gray-500">تاريخ الاستلام</div>
                <div className="font-bold text-xs">{fmtDT(b.received_at)}</div>
              </div>
            </div>

            {b.notes && (
              <div className="border-r-4 border-amber-400 bg-amber-50 p-3 text-sm">
                <b>ملاحظات:</b> {b.notes}
              </div>
            )}
            {b.cancellation_reason && (
              <div className="border-r-4 border-red-400 bg-red-50 p-3 text-sm">
                <b>سبب الإلغاء:</b> {b.cancellation_reason}
              </div>
            )}

            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-sm">
                <thead className="bg-amber-50">
                  <tr>
                    <th className="p-2 text-start">#</th>
                    <th className="p-2 text-start">رقم الوصل</th>
                    <th className="p-2 text-start">التاريخ / الوقت</th>
                    <th className="p-2 text-start">النوع</th>
                    <th className="p-2 text-start">العميل</th>
                    <th className="p-2 text-start">الموظف</th>
                    <th className="p-2 text-start">طريقة الدفع</th>
                    <th className="p-2 text-end">المبلغ</th>
                  </tr>
                </thead>
                <tbody>
                  {entries.map((e, i) => (
                    <tr key={e.id} className="border-t hover:bg-gray-50"
                        data-testid={`batch-entry-row-${e.id}`}>
                      <td className="p-2">{i + 1}</td>
                      <td className="p-2 font-mono font-bold">{e.receipt_no}</td>
                      <td className="p-2 text-xs">{fmtDT(e.created_at)}</td>
                      <td className="p-2">{(e.operation_type || '').replace('_', ' ')}</td>
                      <td className="p-2">{e.client_name || '—'}</td>
                      <td className="p-2">{e.employee_name || '—'}</td>
                      <td className="p-2">{e.payment_method}</td>
                      <td className="p-2 text-end font-bold">{fmtMoney(e.amount, '')}</td>
                    </tr>
                  ))}
                  {!entries.length && (
                    <tr><td colSpan={8} className="p-6 text-center text-gray-400">لا توجد وصولات</td></tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        <DialogFooter className="flex-wrap gap-2">
          <Button variant="outline" onClick={onClose}>إغلاق</Button>
          <Button variant="outline" onClick={print} data-testid="batch-print-btn">🖨️ طباعة الوصل</Button>
          {b.status === 'open' && (
            <Button onClick={doConfirm} disabled={busy}
                    className="bg-amber-600 hover:bg-amber-700 text-white"
                    data-testid="batch-confirm-btn">✅ تأكيد التسليم</Button>
          )}
          {canManage && (b.status === 'confirmed' || b.status === 'open') && (
            <Button onClick={doReceive} disabled={busy}
                    className="bg-green-600 hover:bg-green-700 text-white"
                    data-testid="batch-receive-btn">🏦 تأكيد الاستلام (المالية)</Button>
          )}
          {canManage && b.status !== 'received' && b.status !== 'cancelled' && (
            <Button onClick={doCancel} disabled={busy} variant="destructive"
                    data-testid="batch-cancel-btn">🗑️ إلغاء الحزمة</Button>
          )}
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ════════════════════════════════════════════════════════════════════════
   Entry Detail Dialog (shows timeline)
   ════════════════════════════════════════════════════════════════════════ */
const EntryDetailDialog = ({ open, entryId, onClose }) => {
  const [entry, setEntry] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    if (!open || !entryId) return;
    setLoading(true);
    axios.get(`${API}/financial/entries/${entryId}`, { headers: authHeader() })
      .then((r) => setEntry(r.data))
      .catch(() => toast.error('فشل تحميل الوصل'))
      .finally(() => setLoading(false));
  }, [open, entryId]);

  if (!open) return null;
  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-3xl" dir="rtl">
        <DialogHeader>
          <DialogTitle className="flex items-center justify-between">
            <span>📄 وصل مالي {entry?.receipt_no || ''}</span>
            {entry && <StatusBadge status={entry.status} />}
          </DialogTitle>
          <DialogDescription>عرض كامل لتفاصيل الوصل وخطه الزمني (إنشاء ← مصادقة ← تحويل ← استلام).</DialogDescription>
        </DialogHeader>
        {loading ? (
          <div className="p-8 text-center text-gray-500">جاري التحميل...</div>
        ) : entry ? (
          <div className="space-y-4">
            <div className="grid grid-cols-2 md:grid-cols-3 gap-3 text-sm">
              <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 col-span-2 md:col-span-1">
                <div className="text-xs text-amber-700">المبلغ</div>
                <div className="font-bold text-2xl">{fmtMoney(entry.amount, entry.currency)}</div>
              </div>
              <div><b>الوكالة:</b> {entry.agency_name || '—'}</div>
              <div><b>الموظف:</b> {entry.employee_name || '—'}</div>
              <div><b>العميل:</b> {entry.client_name || '—'}</div>
              <div><b>نوع العملية:</b> {(entry.operation_type || '').replace('_', ' ')}</div>
              <div><b>طريقة الدفع:</b> {entry.payment_method}</div>
              <div><b>تاريخ الإنشاء:</b> {fmtDT(entry.created_at)}</div>
              <div><b>حزمة التحويل:</b> {entry.transfer_batch_ref || '—'}</div>
              <div className="col-span-2 md:col-span-3"><b>وصف:</b> {entry.description || '—'}</div>
            </div>
            <div className="border-t pt-4">
              <h4 className="font-bold mb-2">⏱️ الخط الزمني</h4>
              <Timeline entry={entry} />
            </div>
          </div>
        ) : null}
        <DialogFooter>
          <Button variant="outline" onClick={onClose}>إغلاق</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
};

/* ════════════════════════════════════════════════════════════════════════
   MAIN COMPONENT
   ════════════════════════════════════════════════════════════════════════ */
const FinancialTraceability = () => {
  const [currentUser, setCurrentUser] = useState(null);
  const [tab, setTab] = useState('entries');

  // Entries state
  const [entries, setEntries] = useState([]);
  const [entriesMeta, setEntriesMeta] = useState({ total: 0, total_amount: 0, page: 1 });
  const [entriesLoading, setEntriesLoading] = useState(false);
  const [filters, setFilters] = useState({
    start_date: '', end_date: '', status: '', operation_type: '',
    search: '', unbatched_only: false,
  });
  const [selectedIds, setSelectedIds] = useState(new Set());

  // Batches state
  const [batches, setBatches] = useState([]);
  const [batchesMeta, setBatchesMeta] = useState({ total: 0, sum_total_amount: 0 });
  const [batchesLoading, setBatchesLoading] = useState(false);
  const [batchFilters, setBatchFilters] = useState({ start_date: '', end_date: '', status: '', search: '' });

  // Audit state
  const [auditLog, setAuditLog] = useState([]);
  const [auditLoading, setAuditLoading] = useState(false);

  // Summary
  const [summary, setSummary] = useState({ entries_total: 0, amount_total: 0, by_status: {} });

  // Receipts (by transfer status) state — drives the 4 tabs at top of "Receipts" view
  const [receiptsTab, setReceiptsTab] = useState('non_transferred'); // all | non_transferred | transferred | pending
  const [receipts, setReceipts] = useState([]);
  const [receiptsLoading, setReceiptsLoading] = useState(false);
  const [receiptsMeta, setReceiptsMeta] = useState({ total: 0, total_amount: 0 });
  const [receiptsSummary, setReceiptsSummary] = useState({ buckets: {}, totals: {}, tabs: {} });
  const [receiptsFilters, setReceiptsFilters] = useState({
    start_date: '', end_date: '', employee_id: '', operation_type: '', search: '',
  });
  const [alertsData, setAlertsData] = useState({ items: [], count: 0, amount: 0 });

  // Dialogs
  const [newEntryOpen, setNewEntryOpen] = useState(false);
  const [createBatchOpen, setCreateBatchOpen] = useState(false);
  const [batchDetailOpen, setBatchDetailOpen] = useState(false);
  const [batchDetailId, setBatchDetailId] = useState(null);
  const [entryDetailOpen, setEntryDetailOpen] = useState(false);
  const [entryDetailId, setEntryDetailId] = useState(null);

  useEffect(() => {
    axios.get(`${API}/auth/me`, { headers: authHeader() })
      .then((r) => setCurrentUser(r.data))
      .catch(() => {});
  }, []);

  const canFinanceReceive = useMemo(
    () => currentUser?.role === 'super_admin' || currentUser?.role === 'general_accountant',
    [currentUser],
  );

  /* ----------------- Loaders ----------------- */
  const loadEntries = useCallback(async (overrides = {}) => {
    setEntriesLoading(true);
    try {
      const p = new URLSearchParams({ page: 1, per_page: 100 });
      const merged = { ...filters, ...overrides };
      if (merged.start_date) p.set('start_date', new Date(merged.start_date).toISOString());
      if (merged.end_date)   p.set('end_date',   new Date(merged.end_date + 'T23:59:59').toISOString());
      if (merged.status)         p.set('status', merged.status);
      if (merged.operation_type) p.set('operation_type', merged.operation_type);
      if (merged.search)         p.set('search', merged.search);
      if (merged.unbatched_only) p.set('unbatched_only', 'true');
      const r = await axios.get(`${API}/financial/entries?${p}`, { headers: authHeader() });
      setEntries(r.data.items || []);
      setEntriesMeta({ total: r.data.total, total_amount: r.data.total_amount, page: r.data.page });
    } catch (e) {
      toast.error('فشل تحميل الوصولات');
    } finally {
      setEntriesLoading(false);
    }
  }, [filters]);

  const loadBatches = useCallback(async (overrides = {}) => {
    setBatchesLoading(true);
    try {
      const p = new URLSearchParams({ page: 1, per_page: 100 });
      const merged = { ...batchFilters, ...overrides };
      if (merged.start_date) p.set('start_date', new Date(merged.start_date).toISOString());
      if (merged.end_date)   p.set('end_date',   new Date(merged.end_date + 'T23:59:59').toISOString());
      if (merged.status)     p.set('status', merged.status);
      if (merged.search)     p.set('search', merged.search);
      const r = await axios.get(`${API}/financial/batches?${p}`, { headers: authHeader() });
      setBatches(r.data.items || []);
      setBatchesMeta({ total: r.data.total, sum_total_amount: r.data.sum_total_amount });
    } catch (e) {
      toast.error('فشل تحميل الحزم');
    } finally {
      setBatchesLoading(false);
    }
  }, [batchFilters]);

  const loadAudit = useCallback(async () => {
    setAuditLoading(true);
    try {
      const r = await axios.get(`${API}/financial/audit-log?limit=300`, { headers: authHeader() });
      setAuditLog(r.data.items || []);
    } catch (e) {
      // 403 expected for agency_staff — silently ignore
    } finally {
      setAuditLoading(false);
    }
  }, []);

  const loadSummary = useCallback(async () => {
    try {
      const r = await axios.get(`${API}/financial/summary`, { headers: authHeader() });
      setSummary(r.data);
    } catch (e) {
      // Non-blocking — summary KPIs are best-effort. Log for diagnostics.
      console.warn('[FinancialTraceability] loadSummary failed:', e?.message || e);
    }
  }, []);

  /* ---------- Receipts by transfer-status loaders ---------- */
  const _receiptsParams = useCallback((override = {}, extra = {}) => {
    const p = new URLSearchParams();
    const merged = { ...receiptsFilters, ...override };
    if (merged.start_date) p.set('start_date', new Date(merged.start_date).toISOString());
    if (merged.end_date)   p.set('end_date',   new Date(merged.end_date + 'T23:59:59').toISOString());
    if (merged.employee_id)    p.set('employee_id', merged.employee_id);
    if (merged.operation_type) p.set('operation_type', merged.operation_type);
    if (merged.search)         p.set('search', merged.search);
    Object.entries(extra).forEach(([k, v]) => v != null && p.set(k, v));
    return p;
  }, [receiptsFilters]);

  // Map UI tabs → backend transfer_status param
  const _tabToStatus = (t) => ({
    all: 'all',
    non_transferred: 'available',    // primary classification for this tab
    transferred: 'transferred',
    pending: 'pending',
  }[t] || 'all');

  const loadReceipts = useCallback(async (override = {}, tabOverride = null) => {
    setReceiptsLoading(true);
    try {
      const useTab = tabOverride || receiptsTab;
      const status = _tabToStatus(useTab);
      const p = _receiptsParams(override, { transfer_status: status, page: 1, per_page: 100 });

      // For "non_transferred" we also want the `pending` rows in the same view.
      // Run a parallel query and merge if needed.
      const main = axios.get(`${API}/financial/receipts?${p}`, { headers: authHeader() });
      let pendingPromise = null;
      if (useTab === 'non_transferred') {
        const p2 = _receiptsParams(override, { transfer_status: 'pending', page: 1, per_page: 100 });
        pendingPromise = axios.get(`${API}/financial/receipts?${p2}`, { headers: authHeader() });
      }
      const [m, pe] = await Promise.all([main, pendingPromise]);
      let items = m.data.items || [];
      let total = m.data.total || 0;
      let totalAmount = m.data.total_amount || 0;
      if (pe) {
        items = [...items, ...(pe.data.items || [])]
          .sort((a, b) => new Date(b.created_at) - new Date(a.created_at));
        total += pe.data.total || 0;
        totalAmount += pe.data.total_amount || 0;
      }
      setReceipts(items);
      setReceiptsMeta({ total, total_amount: totalAmount });
    } catch (e) {
      toast.error('فشل تحميل الوصولات');
    } finally {
      setReceiptsLoading(false);
    }
  }, [receiptsTab, _receiptsParams]);

  const loadReceiptsSummary = useCallback(async (override = {}) => {
    try {
      const p = _receiptsParams(override);
      const r = await axios.get(`${API}/financial/receipts/summary?${p}`, { headers: authHeader() });
      setReceiptsSummary(r.data);
    } catch (e) {
      // Non-blocking — KPIs degrade gracefully. Log for diagnostics.
      console.warn('[FinancialTraceability] loadReceiptsSummary failed:', e?.message || e);
    }
  }, [_receiptsParams]);

  const loadAlerts = useCallback(async (staleDays = 30) => {
    try {
      const r = await axios.get(`${API}/financial/receipts/alerts?stale_days=${staleDays}`,
        { headers: authHeader() });
      setAlertsData(r.data);
    } catch (e) {
      // Non-blocking — alerts banner is best-effort. Log for diagnostics.
      console.warn('[FinancialTraceability] loadAlerts failed:', e?.message || e);
    }
  }, []);

  useEffect(() => { loadEntries(); loadSummary(); }, [loadEntries, loadSummary]);
  useEffect(() => { if (tab === 'batches') loadBatches(); }, [tab, loadBatches]);
  useEffect(() => { if (tab === 'audit') loadAudit(); }, [tab, loadAudit]);
  useEffect(() => {
    if (tab === 'receipts') { loadReceipts(); loadReceiptsSummary(); loadAlerts(30); }
  }, [tab, loadReceipts, loadReceiptsSummary, loadAlerts]);

  /* ----------------- Helpers ----------------- */
  const toggleSelect = (entry) => {
    if (entry.transfer_batch_id || entry.status === 'transferred'
        || entry.status === 'received' || entry.status === 'cancelled') {
      toast.warning('هذا الوصل مقفل ولا يمكن إضافته');
      return;
    }
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(entry.id)) next.delete(entry.id);
      else next.add(entry.id);
      return next;
    });
  };

  const selectAllVisible = () => {
    const candidates = entries.filter((e) => !e.transfer_batch_id
      && ['created', 'validated'].includes(e.status));
    setSelectedIds(new Set(candidates.map((e) => e.id)));
  };
  const clearSelection = () => setSelectedIds(new Set());

  const selectedEntries = useMemo(
    () => entries.filter((e) => selectedIds.has(e.id)),
    [entries, selectedIds],
  );
  const selectedTotal = selectedEntries.reduce((s, e) => s + Number(e.amount || 0), 0);

  const validateEntry = async (id) => {
    try {
      await axios.post(`${API}/financial/entries/${id}/validate`, {}, { headers: authHeader() });
      toast.success('✅ تمت المصادقة');
      loadEntries();
      loadSummary();
    } catch (e) {
      toast.error('فشل: ' + (e?.response?.data?.detail || e.message));
    }
  };

  const printEntriesReport = async () => {
    // Reuses printable batch endpoint when we have a single batch context;
    // otherwise produces a quick client-side printable report.
    const w = window.open('', '_blank', 'noopener,noreferrer');
    const totalAmt = entriesMeta.total_amount || 0;
    const rowsHtml = entries.map((e, i) => `
      <tr>
        <td>${i + 1}</td>
        <td><b>${e.receipt_no}</b></td>
        <td>${fmtDT(e.created_at)}</td>
        <td>${(e.operation_type || '').replace('_', ' ')}</td>
        <td>${e.client_name || '—'}</td>
        <td>${e.employee_name || '—'}</td>
        <td>${e.agency_name || '—'}</td>
        <td>${e.payment_method}</td>
        <td>${e.status}</td>
        <td style="text-align:end"><b>${fmtMoney(e.amount, '')}</b></td>
      </tr>`).join('');
    w.document.write(`<!doctype html><html lang="ar" dir="rtl"><head>
      <meta charset="utf-8" /><title>تقرير الوصولات المالية</title>
      <style>
        body{font-family:Cairo,Amiri,Arial,sans-serif;padding:24px;color:#111}
        h1{color:#b8860b;margin:0 0 12px}
        table{width:100%;border-collapse:collapse;font-size:12px}
        th,td{border:1px solid #ddd;padding:6px;text-align:start}
        thead th{background:#fff8e1;color:#7a5d00}
        .total{margin-top:14px;padding:12px 16px;background:#fff8e1;
               border:2px solid #d4af37;font-weight:700;font-size:16px;
               display:flex;justify-content:space-between}
      </style></head><body>
      <h1>🧾 تقرير الوصولات المالية</h1>
      <div>عدد الوصولات: <b>${entries.length}</b> &nbsp; / &nbsp;
           تاريخ التقرير: <b>${fmtDT(new Date().toISOString())}</b></div>
      <table style="margin-top:14px">
        <thead><tr>
          <th>#</th><th>رقم الوصل</th><th>التاريخ</th><th>النوع</th>
          <th>العميل</th><th>الموظف</th><th>الوكالة</th><th>طريقة الدفع</th>
          <th>الحالة</th><th>المبلغ</th>
        </tr></thead><tbody>${rowsHtml}</tbody>
      </table>
      <div class="total"><span>الإجمالي</span><span>${fmtMoney(totalAmt)}</span></div>
    </body></html>`);
    w.document.close();
    setTimeout(() => {
      try { w.print(); }
      catch (err) { console.warn('[FinancialTraceability] entries-report print() failed:', err?.message || err); }
    }, 600);
  };

  /* ----------------- Render ----------------- */
  return (
    <div className="p-4 md:p-6 space-y-5" dir="rtl" data-testid="financial-traceability-page">
      {/* Banner + KPIs */}
      <div className="bg-gradient-to-l from-amber-900 via-yellow-800 to-amber-700 text-amber-50 p-6 rounded-2xl shadow-lg">
        <div className="flex flex-wrap justify-between items-end gap-4">
          <div>
            <h1 className="text-3xl font-bold tracking-wide">🔐 التتبع المالي والتحويلات</h1>
            <p className="text-amber-100 mt-1 text-sm">
              نظام دفتر مالي غير قابل للتعديل — كل عملية لها وصل مرتبط ومسار تدقيق كامل
            </p>
          </div>
          <div className="flex gap-2 flex-wrap">
            <KPI title="إجمالي الوصولات" value={summary.entries_total} testId="kpi-total-count" />
            <KPI title="القيمة الإجمالية" value={fmtMoney(summary.amount_total)} testId="kpi-total-amount" />
            <KPI title="مُستلَم" value={summary.by_status?.received?.count || 0} testId="kpi-received-count" tone="green" />
            <KPI title="غير محوَّل" value={(summary.by_status?.created?.count || 0) + (summary.by_status?.validated?.count || 0)} testId="kpi-pending-count" tone="amber" />
          </div>
        </div>
      </div>

      <Tabs value={tab} onValueChange={setTab}>
        <TabsList className="bg-amber-50 border border-amber-200 rounded-xl p-1 flex-wrap">
          <TabsTrigger value="entries" data-testid="tab-entries">📄 الوصولات المالية</TabsTrigger>
          <TabsTrigger value="receipts" data-testid="tab-receipts">📑 الوصولات حسب الحالة</TabsTrigger>
          <TabsTrigger value="batches" data-testid="tab-batches">🚚 حزم التحويل</TabsTrigger>
          {(canFinanceReceive) && (
            <TabsTrigger value="audit" data-testid="tab-audit">🔍 سجل التدقيق</TabsTrigger>
          )}
          {currentUser?.role === 'super_admin' && (
            <TabsTrigger value="sync_audit" data-testid="tab-sync-audit">🔁 سجل المزامنة</TabsTrigger>
          )}
        </TabsList>

        {/* ════════ TAB: ENTRIES ════════ */}
        <TabsContent value="entries" className="space-y-4">
          {/* Filter bar */}
          <Card>
            <CardContent className="grid grid-cols-2 md:grid-cols-6 gap-3 pt-4">
              <div>
                <Label className="text-xs">بحث (رقم الوصل / وصف)</Label>
                <Input value={filters.search}
                       onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                       onKeyDown={(e) => e.key === 'Enter' && loadEntries()}
                       data-testid="filter-search" placeholder="FIN-..." />
              </div>
              <div>
                <Label className="text-xs">من تاريخ</Label>
                <Input type="date" value={filters.start_date}
                       onChange={(e) => setFilters({ ...filters, start_date: e.target.value })}
                       data-testid="filter-start" />
              </div>
              <div>
                <Label className="text-xs">إلى تاريخ</Label>
                <Input type="date" value={filters.end_date}
                       onChange={(e) => setFilters({ ...filters, end_date: e.target.value })}
                       data-testid="filter-end" />
              </div>
              <div>
                <Label className="text-xs">الحالة</Label>
                <Select value={filters.status || 'all'}
                        onValueChange={(v) => setFilters({ ...filters, status: v === 'all' ? '' : v })}>
                  <SelectTrigger data-testid="filter-status"><SelectValue placeholder="الكل" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">الكل</SelectItem>
                    {Object.entries(STATUS_META).map(([k, v]) =>
                      <SelectItem key={k} value={k}>{v.icon} {v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <Label className="text-xs">نوع العملية</Label>
                <Select value={filters.operation_type || 'all'}
                        onValueChange={(v) => setFilters({ ...filters, operation_type: v === 'all' ? '' : v })}>
                  <SelectTrigger data-testid="filter-op-type"><SelectValue placeholder="الكل" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">الكل</SelectItem>
                    {OPERATION_TYPES.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 items-end">
                <Button onClick={() => loadEntries()} className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="filter-apply">🔍 تطبيق</Button>
                <Button variant="outline" onClick={() => {
                  setFilters({ start_date: '', end_date: '', status: '', operation_type: '', search: '', unbatched_only: false });
                  setTimeout(() => loadEntries({ start_date: '', end_date: '', status: '', operation_type: '', search: '', unbatched_only: false }), 50);
                }}>↺</Button>
              </div>
            </CardContent>
          </Card>

          {/* Action bar */}
          <div className="flex flex-wrap items-center justify-between gap-3 bg-white border rounded-xl p-3 shadow-sm">
            <div className="flex items-center gap-3">
              <Button onClick={() => setNewEntryOpen(true)}
                      className="bg-amber-600 hover:bg-amber-700 text-white"
                      data-testid="open-new-entry-dialog">
                ➕ وصل جديد
              </Button>
              <Button
                variant="outline"
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
                data-testid="sync-from-payments-btn"
                onClick={async () => {
                  if (!window.confirm('سيتم تحويل كل المدفوعات الموجودة إلى وصولات مالية. هذه العملية آمنة (idempotent) ولن تُكرّر الوصولات الموجودة. متابعة؟')) return;
                  try {
                    const r = await axios.post(`${API}/financial/sync/from-payments`, {}, { headers: authHeader() });
                    toast.success(`✅ تم استيراد ${r.data.created} وصل جديد (تخطّى ${r.data.skipped} موجود مسبقاً)`);
                    loadEntries();
                    loadSummary();
                  } catch (e) {
                    toast.error('فشل المزامنة: ' + (e?.response?.data?.detail || e.message));
                  }
                }}
              >
                🔄 استيراد من المدفوعات/العمليات
              </Button>
              <Button variant="outline" onClick={printEntriesReport} data-testid="print-entries-report">
                🖨️ طباعة تقرير
              </Button>
              <label className="text-xs inline-flex items-center gap-1 cursor-pointer">
                <input type="checkbox" checked={filters.unbatched_only}
                       onChange={(e) => {
                         const v = e.target.checked;
                         setFilters((f) => ({ ...f, unbatched_only: v }));
                         setTimeout(() => loadEntries({ unbatched_only: v }), 50);
                       }}
                       data-testid="filter-unbatched-only" />
                إظهار غير المُحوَّلة فقط
              </label>
            </div>
            <div className="flex items-center gap-2 flex-wrap">
              <span className="text-sm text-gray-700">
                المحدَّد: <b>{selectedIds.size}</b> &nbsp;|&nbsp; الإجمالي:
                <b className="text-amber-700"> {fmtMoney(selectedTotal)}</b>
              </span>
              <Button variant="outline" size="sm" onClick={selectAllVisible}>تحديد الكل</Button>
              <Button variant="outline" size="sm" onClick={clearSelection}>مسح</Button>
              <Button onClick={() => setCreateBatchOpen(true)}
                      disabled={!selectedIds.size}
                      className="bg-green-600 hover:bg-green-700 text-white disabled:opacity-40"
                      data-testid="create-batch-btn">
                🚚 إنشاء حزمة تحويل
              </Button>
            </div>
          </div>

          {/* Entries table */}
          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex justify-between items-center">
                <span>📄 قائمة الوصولات</span>
                <span className="text-xs text-gray-500">
                  العدد: {entriesMeta.total} — الإجمالي: <b>{fmtMoney(entriesMeta.total_amount)}</b>
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {entriesLoading ? (
                <div className="p-8 text-center text-gray-500">جاري التحميل...</div>
              ) : entries.length === 0 ? (
                <div className="p-12 text-center text-gray-400">
                  <div className="text-5xl mb-2">📭</div>
                  <p>لا توجد وصولات مطابقة للفلتر</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="entries-table">
                    <thead className="bg-amber-50">
                      <tr>
                        <th className="p-2 w-8"></th>
                        <th className="p-2 text-start">رقم الوصل</th>
                        <th className="p-2 text-start">التاريخ / الوقت</th>
                        <th className="p-2 text-start">النوع</th>
                        <th className="p-2 text-start">العميل</th>
                        <th className="p-2 text-start">الموظف</th>
                        <th className="p-2 text-start">طريقة الدفع</th>
                        <th className="p-2 text-end">المبلغ</th>
                        <th className="p-2 text-start">الحالة</th>
                        <th className="p-2 text-start">الحزمة</th>
                        <th className="p-2 text-start">⚙️</th>
                      </tr>
                    </thead>
                    <tbody>
                      {entries.map((e) => {
                        const locked = !!e.transfer_batch_id || ['transferred', 'received', 'cancelled'].includes(e.status);
                        const checked = selectedIds.has(e.id);
                        return (
                          <tr key={e.id} className={`border-t hover:bg-amber-50/40 ${checked ? 'bg-amber-50' : ''}`}
                              data-testid={`entry-row-${e.id}`}>
                            <td className="p-2">
                              <input type="checkbox" checked={checked} disabled={locked}
                                     onChange={() => toggleSelect(e)}
                                     data-testid={`entry-check-${e.id}`} />
                            </td>
                            <td className="p-2 font-mono font-bold text-xs">{e.receipt_no}</td>
                            <td className="p-2 text-xs">{fmtDT(e.created_at)}</td>
                            <td className="p-2 text-xs">{(e.operation_type || '').replace('_', ' ')}</td>
                            <td className="p-2">{e.client_name || '—'}</td>
                            <td className="p-2 text-xs">{e.employee_name || '—'}</td>
                            <td className="p-2 text-xs">{e.payment_method}</td>
                            <td className="p-2 text-end font-bold">{fmtMoney(e.amount, '')}</td>
                            <td className="p-2"><StatusBadge status={e.status} /></td>
                            <td className="p-2 text-xs font-mono">{e.transfer_batch_ref || '—'}</td>
                            <td className="p-2">
                              <div className="flex gap-1 flex-wrap">
                                <Button size="sm" variant="outline" className="text-xs px-2 py-1"
                                        onClick={() => { setEntryDetailId(e.id); setEntryDetailOpen(true); }}
                                        data-testid={`entry-detail-${e.id}`}>
                                  👁️
                                </Button>
                                {e.status === 'created' && (
                                  <Button size="sm" variant="outline" className="text-xs px-2 py-1 text-amber-700 border-amber-300"
                                          onClick={() => validateEntry(e.id)}
                                          data-testid={`entry-validate-${e.id}`}>
                                    ✅
                                  </Button>
                                )}
                                {e.transfer_batch_id && (
                                  <Button size="sm" variant="outline" className="text-xs px-2 py-1"
                                          onClick={() => { setBatchDetailId(e.transfer_batch_id); setBatchDetailOpen(true); }}
                                          data-testid={`entry-open-batch-${e.id}`}>
                                    🚚
                                  </Button>
                                )}
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ════════ TAB: BATCHES ════════ */}
        <TabsContent value="batches" className="space-y-4">
          <Card>
            <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-3 pt-4">
              <div>
                <Label className="text-xs">بحث (مرجع / ملاحظات)</Label>
                <Input value={batchFilters.search}
                       onChange={(e) => setBatchFilters({ ...batchFilters, search: e.target.value })}
                       onKeyDown={(e) => e.key === 'Enter' && loadBatches()}
                       data-testid="batch-filter-search" placeholder="BATCH-..." />
              </div>
              <div>
                <Label className="text-xs">من تاريخ</Label>
                <Input type="date" value={batchFilters.start_date}
                       onChange={(e) => setBatchFilters({ ...batchFilters, start_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">إلى تاريخ</Label>
                <Input type="date" value={batchFilters.end_date}
                       onChange={(e) => setBatchFilters({ ...batchFilters, end_date: e.target.value })} />
              </div>
              <div>
                <Label className="text-xs">الحالة</Label>
                <Select value={batchFilters.status || 'all'}
                        onValueChange={(v) => setBatchFilters({ ...batchFilters, status: v === 'all' ? '' : v })}>
                  <SelectTrigger data-testid="batch-filter-status"><SelectValue placeholder="الكل" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">الكل</SelectItem>
                    {Object.entries(BATCH_STATUS_META).map(([k, v]) =>
                      <SelectItem key={k} value={k}>{v.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 items-end">
                <Button onClick={() => loadBatches()} className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="batch-filter-apply">🔍 تطبيق</Button>
                <Button variant="outline" onClick={() => {
                  setBatchFilters({ start_date: '', end_date: '', status: '', search: '' });
                  setTimeout(() => loadBatches({ start_date: '', end_date: '', status: '', search: '' }), 50);
                }}>↺</Button>
              </div>
            </CardContent>
          </Card>

          <Card>
            <CardHeader className="pb-2">
              <CardTitle className="text-base flex justify-between items-center">
                <span>🚚 حزم التحويل</span>
                <span className="text-xs text-gray-500">
                  العدد: {batchesMeta.total} — المجموع: <b>{fmtMoney(batchesMeta.sum_total_amount)}</b>
                </span>
              </CardTitle>
            </CardHeader>
            <CardContent>
              {batchesLoading ? (
                <div className="p-8 text-center text-gray-500">جاري التحميل...</div>
              ) : batches.length === 0 ? (
                <div className="p-12 text-center text-gray-400">
                  <div className="text-5xl mb-2">📦</div>
                  <p>لا توجد حزم تحويل</p>
                </div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm" data-testid="batches-table">
                    <thead className="bg-amber-50">
                      <tr>
                        <th className="p-2 text-start">المرجع</th>
                        <th className="p-2 text-start">التاريخ</th>
                        <th className="p-2 text-start">الوكالة</th>
                        <th className="p-2 text-start">المُرسِل</th>
                        <th className="p-2 text-start">المُستلِم</th>
                        <th className="p-2 text-end">عدد الوصولات</th>
                        <th className="p-2 text-end">الإجمالي</th>
                        <th className="p-2 text-start">الحالة</th>
                        <th className="p-2 text-start">⚙️</th>
                      </tr>
                    </thead>
                    <tbody>
                      {batches.map((b) => (
                        <tr key={b.id} className="border-t hover:bg-amber-50/40"
                            data-testid={`batch-row-${b.id}`}>
                          <td className="p-2 font-mono font-bold">{b.reference_no}</td>
                          <td className="p-2 text-xs">{fmtDT(b.created_at)}</td>
                          <td className="p-2">{b.agency_name || '—'}</td>
                          <td className="p-2 text-xs">{b.created_by_name || '—'}</td>
                          <td className="p-2 text-xs">{b.recipient_name || '—'}</td>
                          <td className="p-2 text-end">{b.entries_count}</td>
                          <td className="p-2 text-end font-bold text-amber-700">
                            {fmtMoney(b.total_amount, b.currency || 'DZD')}
                          </td>
                          <td className="p-2"><StatusBadge status={b.status} map={BATCH_STATUS_META} /></td>
                          <td className="p-2">
                            <Button size="sm" variant="outline" className="text-xs px-2 py-1"
                                    onClick={() => { setBatchDetailId(b.id); setBatchDetailOpen(true); }}
                                    data-testid={`batch-open-${b.id}`}>
                              👁️ عرض
                            </Button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* ════════ TAB: AUDIT LOG ════════ */}
        {canFinanceReceive && (
          <TabsContent value="audit" className="space-y-4">
            <Card>
              <CardHeader>
                <CardTitle className="text-base flex justify-between items-center">
                  <span>🔍 سجل التدقيق المالي</span>
                  <Badge variant="outline">آخر {auditLog.length} حدث</Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {auditLoading ? (
                  <div className="p-8 text-center text-gray-500">جاري التحميل...</div>
                ) : auditLog.length === 0 ? (
                  <div className="p-12 text-center text-gray-400">
                    <p>لا توجد أحداث مسجَّلة</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm" data-testid="audit-table">
                      <thead className="bg-gray-50">
                        <tr>
                          <th className="p-2 text-start">التاريخ / الوقت</th>
                          <th className="p-2 text-start">الإجراء</th>
                          <th className="p-2 text-start">النوع</th>
                          <th className="p-2 text-start">المعرف</th>
                          <th className="p-2 text-start">الفاعل</th>
                          <th className="p-2 text-start">الدور</th>
                          <th className="p-2 text-start">IP</th>
                        </tr>
                      </thead>
                      <tbody>
                        {auditLog.map((a) => (
                          <tr key={a.id} className="border-t hover:bg-gray-50"
                              data-testid={`audit-row-${a.id}`}>
                            <td className="p-2 text-xs whitespace-nowrap">{fmtDT(a.at)}</td>
                            <td className="p-2 font-mono text-xs">{a.action}</td>
                            <td className="p-2 text-xs">{a.entity_type}</td>
                            <td className="p-2 text-xs font-mono">{(a.entity_id || '').slice(0, 8)}</td>
                            <td className="p-2 text-xs">{a.actor_name || '—'}</td>
                            <td className="p-2 text-xs">{a.actor_role}</td>
                            <td className="p-2 text-xs font-mono">{a.ip || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* ════════ TAB: RECEIPTS BY TRANSFER STATUS ════════ */}
        <TabsContent value="receipts" className="space-y-4">
          {/* KPI strip — total amount per category */}
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
            {[
              { key: 'available',   label: '✅ متاح للتحويل', tone: 'bg-blue-50 border-blue-200 text-blue-900' },
              { key: 'pending',     label: '⏳ معلَّق',         tone: 'bg-amber-50 border-amber-200 text-amber-900' },
              { key: 'transferred', label: '🏦 محوَّل',         tone: 'bg-green-50 border-green-200 text-green-900' },
              { key: 'cancelled',   label: '✖️ مُلغى',          tone: 'bg-gray-50 border-gray-200 text-gray-700' },
              { key: 'refunded',    label: '↩️ مُستَرجَع',      tone: 'bg-purple-50 border-purple-200 text-purple-900' },
            ].map((k) => {
              const b = (receiptsSummary.buckets || {})[k.key] || { count: 0, amount: 0 };
              return (
                <div key={k.key} className={`rounded-xl border-2 p-3 ${k.tone}`}
                     data-testid={`receipts-kpi-${k.key}`}>
                  <div className="text-xs font-medium">{k.label}</div>
                  <div className="text-2xl font-bold mt-1">{fmtMoney(b.amount)}</div>
                  <div className="text-xs opacity-75 mt-0.5">{b.count} وصل</div>
                </div>
              );
            })}
          </div>

          {/* Stale-receipts alert banner */}
          {alertsData.count > 0 && (
            <div className="bg-gradient-to-l from-red-50 to-orange-50 border-2 border-red-300 rounded-xl p-4 flex items-center justify-between flex-wrap gap-3"
                 data-testid="receipts-alerts-banner">
              <div className="flex items-center gap-3">
                <div className="text-3xl">🚨</div>
                <div>
                  <div className="font-bold text-red-800">
                    {alertsData.count} وصل متاح للتحويل منذ أكثر من 30 يومًا
                  </div>
                  <div className="text-sm text-red-700">
                    إجمالي مُعلَّق: <b>{fmtMoney(alertsData.amount)}</b> دج
                  </div>
                </div>
              </div>
              <Button size="sm" variant="outline" className="border-red-300 text-red-700 hover:bg-red-50"
                      onClick={() => {
                        setReceiptsTab('non_transferred');
                        setTimeout(() => loadReceipts({}, 'non_transferred'), 50);
                      }}
                      data-testid="receipts-alerts-jump">
                ↩ عرض الوصولات المتأخرة
              </Button>
            </div>
          )}

          {/* Filters */}
          <Card>
            <CardContent className="grid grid-cols-2 md:grid-cols-5 gap-3 pt-4">
              <div>
                <Label className="text-xs">بحث (رقم الوصل / وصف / عميل)</Label>
                <Input value={receiptsFilters.search}
                       onChange={(e) => setReceiptsFilters({ ...receiptsFilters, search: e.target.value })}
                       onKeyDown={(e) => e.key === 'Enter' && (loadReceipts(), loadReceiptsSummary())}
                       data-testid="receipts-filter-search" placeholder="FIN-..." />
              </div>
              <div>
                <Label className="text-xs">من تاريخ</Label>
                <Input type="date" value={receiptsFilters.start_date}
                       onChange={(e) => setReceiptsFilters({ ...receiptsFilters, start_date: e.target.value })}
                       data-testid="receipts-filter-start" />
              </div>
              <div>
                <Label className="text-xs">إلى تاريخ</Label>
                <Input type="date" value={receiptsFilters.end_date}
                       onChange={(e) => setReceiptsFilters({ ...receiptsFilters, end_date: e.target.value })}
                       data-testid="receipts-filter-end" />
              </div>
              <div>
                <Label className="text-xs">نوع العملية</Label>
                <Select value={receiptsFilters.operation_type || 'all'}
                        onValueChange={(v) => setReceiptsFilters({ ...receiptsFilters, operation_type: v === 'all' ? '' : v })}>
                  <SelectTrigger data-testid="receipts-filter-op-type"><SelectValue placeholder="الكل" /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">الكل</SelectItem>
                    {OPERATION_TYPES.map((o) => (
                      <SelectItem key={o.value} value={o.value}>{o.label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div className="flex gap-2 items-end">
                <Button onClick={() => { loadReceipts(); loadReceiptsSummary(); }}
                        className="bg-amber-600 hover:bg-amber-700 text-white"
                        data-testid="receipts-filter-apply">🔍 تطبيق</Button>
                <Button variant="outline" onClick={() => {
                  const cleared = { start_date: '', end_date: '', employee_id: '', operation_type: '', search: '' };
                  setReceiptsFilters(cleared);
                  setTimeout(() => { loadReceipts(cleared); loadReceiptsSummary(cleared); }, 50);
                }}>↺</Button>
              </div>
            </CardContent>
          </Card>

          {/* Inner tabs */}
          <Tabs value={receiptsTab} onValueChange={(v) => {
            setReceiptsTab(v);
            setTimeout(() => loadReceipts({}, v), 30);
          }}>
            <TabsList className="bg-white border border-amber-200 rounded-xl p-1 flex-wrap">
              <TabsTrigger value="all" data-testid="r-tab-all">
                📋 الكل ({receiptsSummary.tabs?.all?.count || 0})
              </TabsTrigger>
              <TabsTrigger value="non_transferred" data-testid="r-tab-non-transferred">
                🟡 غير المُحوَّلة ({receiptsSummary.tabs?.non_transferred?.count || 0})
              </TabsTrigger>
              <TabsTrigger value="transferred" data-testid="r-tab-transferred">
                🟢 المُحوَّلة ({receiptsSummary.tabs?.transferred?.count || 0})
              </TabsTrigger>
              <TabsTrigger value="pending" data-testid="r-tab-pending">
                🟠 المُعلَّقة ({receiptsSummary.tabs?.pending?.count || 0})
              </TabsTrigger>
            </TabsList>

            <TabsContent value={receiptsTab}>
              <Card>
                <CardHeader className="pb-2">
                  <CardTitle className="text-base flex justify-between items-center">
                    <span>📑 الوصولات حسب الحالة</span>
                    <span className="text-xs text-gray-600">
                      عدد: <b>{receiptsMeta.total}</b> — الإجمالي: <b className="text-amber-700">{fmtMoney(receiptsMeta.total_amount)}</b>
                    </span>
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  {receiptsLoading ? (
                    <div className="p-8 text-center text-gray-500">جاري التحميل...</div>
                  ) : receipts.length === 0 ? (
                    <div className="p-12 text-center text-gray-400">
                      <div className="text-5xl mb-2">📭</div>
                      <p>لا توجد وصولات في هذه الفئة</p>
                    </div>
                  ) : (
                    <div className="overflow-x-auto">
                      <table className="w-full text-sm" data-testid="receipts-table">
                        <thead className="bg-amber-50">
                          <tr>
                            <th className="p-2 text-start">رقم الوصل</th>
                            <th className="p-2 text-start">التاريخ / الوقت</th>
                            <th className="p-2 text-start">النوع</th>
                            <th className="p-2 text-start">العميل</th>
                            <th className="p-2 text-start">الموظف</th>
                            <th className="p-2 text-start">طريقة الدفع</th>
                            <th className="p-2 text-end">المبلغ</th>
                            <th className="p-2 text-start">حالة التحويل</th>
                            <th className="p-2 text-start">المرتبط بـ</th>
                            <th className="p-2 text-start">⚙️</th>
                          </tr>
                        </thead>
                        <tbody>
                          {receipts.map((e) => {
                            const ts = e.transfer_status || 'available';
                            const tsMap = {
                              available:   { label: '✅ متاح',    cls: 'bg-blue-100 text-blue-800 border-blue-300' },
                              pending:     { label: '⏳ معلَّق',  cls: 'bg-amber-100 text-amber-800 border-amber-300' },
                              transferred: { label: '🏦 محوَّل',  cls: 'bg-green-100 text-green-800 border-green-300' },
                              cancelled:   { label: '✖️ مُلغى',   cls: 'bg-gray-200 text-gray-700 border-gray-300' },
                              refunded:    { label: '↩️ مُستَرجَع', cls: 'bg-purple-100 text-purple-800 border-purple-300' },
                            };
                            const m = tsMap[ts] || tsMap.available;
                            const linkRef = e.cash_transfer_ref || e.transfer_batch_ref;
                            return (
                              <tr key={e.id} className="border-t hover:bg-amber-50/40"
                                  data-testid={`receipts-row-${e.id}`}>
                                <td className="p-2 font-mono font-bold text-xs">{e.receipt_no}</td>
                                <td className="p-2 text-xs whitespace-nowrap">{fmtDT(e.created_at)}</td>
                                <td className="p-2 text-xs">{(e.operation_type || '').replace('_', ' ')}</td>
                                <td className="p-2">{e.client_name || '—'}</td>
                                <td className="p-2 text-xs">{e.employee_name || '—'}</td>
                                <td className="p-2 text-xs">{e.payment_method}</td>
                                <td className="p-2 text-end font-bold">{fmtMoney(e.amount, '')}</td>
                                <td className="p-2">
                                  <span className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-semibold border ${m.cls}`}
                                        data-testid={`receipts-status-${e.id}`}>
                                    {m.label}
                                  </span>
                                </td>
                                <td className="p-2 text-xs font-mono">{linkRef || '—'}</td>
                                <td className="p-2">
                                  <div className="flex gap-1">
                                    <Button size="sm" variant="outline" className="text-xs px-2 py-1"
                                            onClick={() => { setEntryDetailId(e.id); setEntryDetailOpen(true); }}
                                            data-testid={`receipts-view-${e.id}`}>👁️</Button>
                                    {e.transfer_batch_id && (
                                      <Button size="sm" variant="outline"
                                              className="text-xs px-2 py-1 text-blue-700 border-blue-300"
                                              onClick={() => { setBatchDetailId(e.transfer_batch_id); setBatchDetailOpen(true); }}
                                              data-testid={`receipts-open-batch-${e.id}`}>🚚</Button>
                                    )}
                                  </div>
                                </td>
                              </tr>
                            );
                          })}
                        </tbody>
                      </table>
                    </div>
                  )}
                </CardContent>
              </Card>
            </TabsContent>
          </Tabs>
        </TabsContent>

        {/* ════════ TAB: FINANCIAL SYNC AUDIT (super_admin only) ════════ */}
        {currentUser?.role === 'super_admin' && (
          <TabsContent value="sync_audit" className="space-y-4">
            <FinancialSyncAudit currentUser={currentUser} />
          </TabsContent>
        )}
      </Tabs>

      {/* Dialogs */}
      <NewEntryDialog open={newEntryOpen} onClose={() => setNewEntryOpen(false)}
                      onCreated={() => { loadEntries(); loadSummary(); }} />
      <CreateBatchDialog open={createBatchOpen} onClose={() => setCreateBatchOpen(false)}
                         selectedEntries={selectedEntries}
                         onCreated={() => { clearSelection(); loadEntries(); loadBatches(); loadSummary(); setTab('batches'); }} />
      <BatchDetailDialog open={batchDetailOpen} batchId={batchDetailId}
                         onClose={() => { setBatchDetailOpen(false); setBatchDetailId(null); }}
                         currentUser={currentUser}
                         onChanged={() => { loadBatches(); loadEntries(); loadSummary(); }} />
      <EntryDetailDialog open={entryDetailOpen} entryId={entryDetailId}
                         onClose={() => { setEntryDetailOpen(false); setEntryDetailId(null); }} />
    </div>
  );
};

const KPI = ({ title, value, testId, tone }) => {
  const toneCls = {
    green: 'bg-green-900/40 ring-green-400/40',
    amber: 'bg-amber-900/40 ring-amber-400/40',
  }[tone] || 'bg-black/30 ring-amber-200/30';
  return (
    <div className={`backdrop-blur rounded-xl px-4 py-2 text-center ring-1 ${toneCls}`}>
      <div className="text-2xl font-bold" data-testid={testId}>{value}</div>
      <div className="text-xs opacity-90">{title}</div>
    </div>
  );
};

export default FinancialTraceability;
